import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

interface CollapsibleWidgetProps {
  id: string;                   // localStorage key suffix — stored as `ridgen-widget-{id}`
  title: string;                // Header text (e.g., "UPCOMING TASKS")
  titleExtra?: React.ReactNode; // Right side of header (e.g., count badge)
  summary?: React.ReactNode;    // Inline right-side summary shown when collapsed (inside header row)
  children: React.ReactNode;    // Full content shown when expanded
  defaultExpanded?: boolean;    // Default state (true if omitted)
  className?: string;
  'data-testid'?: string;
}

export function CollapsibleWidget({
  id,
  title,
  titleExtra,
  summary,
  children,
  defaultExpanded = true,
  className,
  'data-testid': dataTestId,
}: CollapsibleWidgetProps) {
  const storageKey = `ridgen-widget-${id}`;
  const contentId = `${id}-content`;

  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    const persisted = localStorage.getItem(storageKey);
    if (persisted !== null) {
      return persisted !== 'false';
    }
    return defaultExpanded;
  });

  function handleToggle() {
    const next = !isExpanded;
    setIsExpanded(next);
    localStorage.setItem(storageKey, String(next));
  }

  // Collapsed: fully rounded (self-contained stowed module)
  // Expanded header: rounded top only (seats into content below)
  const headerRounded = isExpanded
    ? 'rounded-t-lg rounded-b-none'
    : 'rounded-lg';

  return (
    <div
      data-state={isExpanded ? 'open' : 'closed'}
      data-testid={dataTestId}
      className={`${className ?? ''}`}
    >
      {/* Header button — full row is tap target */}
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={isExpanded}
        aria-controls={contentId}
        style={{ minHeight: '44px' }}
        className={`w-full flex items-center justify-between px-4 py-2 bg-[var(--color-surface)] border border-[var(--color-border)] ${headerRounded}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="field-label">
            {title}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0 min-w-0">
          {/* Summary text — visible only in collapsed state, inside the header row */}
          {!isExpanded && summary !== undefined && (
            <span
              className="text-xs text-[var(--color-text-muted)] truncate max-w-[180px]"
              data-testid="summary-content"
            >
              {summary}
            </span>
          )}
          {titleExtra !== undefined && (
            <span>{titleExtra}</span>
          )}
          <ChevronDown
            size={16}
            aria-hidden
            style={{
              color: 'var(--color-text-muted)',
              transition: 'transform 200ms ease',
              transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
            }}
          />
        </div>
      </button>

      {/* Collapsible content region — visible only when expanded */}
      <div
        id={contentId}
        role="region"
        style={{
          display: 'grid',
          gridTemplateRows: isExpanded ? '1fr' : '0fr',
          transition: 'grid-template-rows 300ms ease',
        }}
      >
        <div style={{ overflow: 'hidden' }}>
          {children}
        </div>
      </div>
    </div>
  );
}
